import React from 'react';
import {
	Alert,
	Badge,
	Collapse,
	Descriptions,
	Divider,
	Row,
	Col,
	Space,
	Statistic,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	EditOutlined,
	ExclamationCircleOutlined,
	ExperimentOutlined,
	MedicineBoxOutlined,
	UserOutlined,
	WarningOutlined,
} from '@ant-design/icons';
import { ImportWarning, XmlImportJobStatus } from '../../../api/healthcare/PatientApi';
import SectionCard from '../../../components/shared/SectionCard';
import DataTable from '../../../components/shared/DataTable';
import StatusTag from '../../../components/shared/StatusTag';

const { Text, Title } = Typography;

/** Converts a test_type value like 'liver_function' → 'Liver Function', keeps short codes uppercase */
function formatTestType(value: string): string {
	const UPPERCASE_CODES = new Set([
		'cmp', 'bmp', 'cbc', 'cbc_diff', 'lft', 'tsh', 'crp', 'hs_crp', 'esr',
		'psa', 'psa_blood', 'free_psa', 'fsh', 'lh', 'amh', 'hgh', 'ana', 'rf',
		'inr', 'pt', 'ptt', 'bnp', 'nt_probnp', 'ldh', 'cea', 'afp', 'shbg',
		'dhea_s', 'ebv_panel', 'cmv_panel', 'hbsag', 'hbsab', 'hcv_ab', 'hiv_combo',
		'dna', 'abg', 'vbg', 'ecg', 'eeg', 'emg', 'ercp',
	]);
	if (UPPERCASE_CODES.has(value)) return value.toUpperCase().replace(/_/g, '-');
	return value
		.split('_')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}

type ResultRecord = {
	id: number;
	name: string;
	date: string | null;
	test_types: string[];
	category: string | null;
	status: string;
	is_abnormal: boolean;
	reviewed_by: string | null;
	requesting_doctor: string | null;
	lab_name: string | null;
	source_page?: number;
};
type MedicationRecord = { id: number; drug: string; dose: string; frequency: string; active: boolean; source_page?: number };
type AppointmentRecord = { id: number; date: string; type: string; summary: string; source_page?: number };

const SOURCE_FORMAT_LABELS: Record<'md_xml' | 'pdf', string> = {
	md_xml: 'Medical Director XML',
	pdf: 'PDF record',
};

// Only a PDF import carries page provenance, so the column is added per-table rather than sitting empty on an XML import.
function sourcePageColumn<T extends { source_page?: number }>(): ColumnsType<T>[number] {
	return {
		title: 'Page',
		dataIndex: 'source_page',
		key: 'source_page',
		width: 80,
		render: (value?: number) =>
			value ? (
				<Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>p. {value}</Text>
			) : (
				<Text type="secondary" style={{ fontSize: 12 }}>—</Text>
			),
	};
}

interface Props {
	jobStatus: XmlImportJobStatus | null;
	pollError: string | null;
	onClose?: () => void;
}

const ImportSuccessModal: React.FC<Props> = ({ jobStatus, pollError }) => {
	if (!jobStatus) return null;

	const isSuccess = jobStatus.status === 'completed';
	const isPartial = jobStatus.status === 'partial';
	const isFailed = jobStatus.status === 'failed';
	const hasError = Boolean(pollError || jobStatus.error_summary);

	const totalRecords =
		jobStatus.section_results && Object.values(jobStatus.section_results).reduce((sum, r) => sum + r.count, 0);

	const duration = (() => {
		if (jobStatus.created_at && jobStatus.completed_at) {
			const start = new Date(jobStatus.created_at).getTime();
			const end = new Date(jobStatus.completed_at).getTime();
			const diffSeconds = Math.floor((end - start) / 1000);
			const minutes = Math.floor(diffSeconds / 60);
			const seconds = diffSeconds % 60;
			return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
		}
		return '-';
	})();

	const results = jobStatus.section_details?.results ?? [];
	const medications = jobStatus.section_details?.medications ?? [];
	const appointments = jobStatus.section_details?.appointments ?? [];

	const sourceFormat = jobStatus.source_format === 'pdf' ? 'pdf' : 'md_xml';
	const titlePrefix = sourceFormat === 'pdf' ? 'PDF ' : '';

	// The backend shares one collector across sections, so the same warning is recorded on every
	// section it ran through — de-duplicate on code + field, the collector's own identity key.
	const warnings: ImportWarning[] = (() => {
		const seen = new Set<string>();
		const collected: ImportWarning[] = [];
		const push = (list?: ImportWarning[]) =>
			(list ?? []).forEach((warning) => {
				const key = `${warning.code}|${warning.field}`;
				if (seen.has(key)) return;
				seen.add(key);
				collected.push(warning);
			});
		Object.values(jobStatus.section_results ?? {}).forEach((section) => push(section.warnings));
		push(jobStatus.section_details?.warnings);
		return collected;
	})();

	const resultsColumns: ColumnsType<ResultRecord> = [
		{
			title: 'Date Performed',
			dataIndex: 'date',
			key: 'date',
			width: 130,
			defaultSortOrder: 'descend',
			sorter: (a, b) => {
				if (!a.date && !b.date) return 0;
				if (!a.date) return 1;
				if (!b.date) return -1;
				return a.date.localeCompare(b.date);
			},
			render: (value: string | null) =>
				value ? <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{value}</Text> : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>,
		},
		{
			title: 'Test Performed',
			key: 'test',
			render: (_: unknown, record: ResultRecord) => (
				<Space direction="vertical" size={4} style={{ width: '100%' }}>
					<Text style={{ fontSize: 12, fontWeight: 500 }}>{record.name}</Text>
					{record.test_types.length > 0 && (
						<Space size={4} wrap>
							{record.test_types.map((t) => (
								<Tag key={t} color="blue" style={{ fontSize: 11, margin: 0, lineHeight: '18px' }}>
									{formatTestType(t)}
								</Tag>
							))}
						</Space>
					)}
				</Space>
			),
		},
		{
			title: 'Lab',
			dataIndex: 'lab_name',
			key: 'lab_name',
			width: 160,
			render: (value: string | null) =>
				value ? <Text style={{ fontSize: 12 }}>{value}</Text> : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>,
		},
		{
			title: 'Reviewed By',
			dataIndex: 'reviewed_by',
			key: 'reviewed_by',
			width: 170,
			render: (value: string | null, record: ResultRecord) => (
				<Space direction="vertical" size={2}>
					{value ? (
						<Text style={{ fontSize: 12 }}>{value}</Text>
					) : (
						<Text type="secondary" style={{ fontSize: 12 }}>—</Text>
					)}
					{record.requesting_doctor && record.requesting_doctor !== value && (
						<Tooltip title="Requesting doctor">
							<Text type="secondary" style={{ fontSize: 11 }}>
								Req: {record.requesting_doctor}
							</Text>
						</Tooltip>
					)}
				</Space>
			),
		},
		{
			title: 'Result',
			key: 'result',
			width: 110,
			render: (_: unknown, record: ResultRecord) => (
				<Space size={4} direction="vertical">
					<StatusTag status={record.status} />
					{record.is_abnormal && (
						<Tag color="red" style={{ fontSize: 11, margin: 0 }}>
							Abnormal
						</Tag>
					)}
				</Space>
			),
		},
		...(results.some((r) => r.source_page != null) ? [sourcePageColumn<ResultRecord>()] : []),
	];

	const medicationColumns: ColumnsType<MedicationRecord> = [
		{ title: 'Drug Name', dataIndex: 'drug', key: 'drug' },
		{ title: 'Dose', dataIndex: 'dose', key: 'dose' },
		{ title: 'Frequency', dataIndex: 'frequency', key: 'frequency' },
		{
			title: 'Status',
			dataIndex: 'active',
			key: 'active',
			render: (active: boolean) => <StatusTag status={active ? 'active' : 'inactive'} />,
		},
		...(medications.some((m) => m.source_page != null) ? [sourcePageColumn<MedicationRecord>()] : []),
	];

	const appointmentColumns: ColumnsType<AppointmentRecord> = [
		{ title: 'Date', dataIndex: 'date', key: 'date', render: (value: string) => value || '-' },
		{ title: 'Type', dataIndex: 'type', key: 'type', render: (value: string) => <StatusTag status={value} label={value} /> },
		{ title: 'Summary', dataIndex: 'summary', key: 'summary' },
		...(appointments.some((a) => a.source_page != null) ? [sourcePageColumn<AppointmentRecord>()] : []),
	];

	return (
		<Space direction="vertical" size={16} style={{ width: '100%' }}>
			<div style={{ textAlign: 'center', padding: '16px 0' }}>
				{isSuccess && <CheckCircleOutlined style={{ fontSize: 48, color: 'var(--mg-success)' }} />}
				{isPartial && <WarningOutlined style={{ fontSize: 48, color: 'var(--mg-warning)' }} />}
				{isFailed && <CloseCircleOutlined style={{ fontSize: 48, color: 'var(--mg-danger)' }} />}
				{hasError && <ExclamationCircleOutlined style={{ fontSize: 48, color: 'var(--mg-warning)' }} />}

				<div style={{ marginTop: 12 }}>
					<Title level={4} style={{ marginBottom: 2 }}>
						{pollError
							? 'Connection Lost'
							: isSuccess
								? `${titlePrefix}Import Completed Successfully`
								: isPartial
									? `${titlePrefix}Import Completed with Warnings`
									: isFailed
										? `${titlePrefix}Import Failed`
										: `${titlePrefix}Import Status Unknown`}
					</Title>
					{jobStatus.patient_name_snapshot && (
						<Text type="secondary" style={{ fontSize: 14 }}>
							{jobStatus.patient_name_snapshot}
						</Text>
					)}
				</div>

				<div style={{ marginTop: 12, display: 'flex', justifyContent: 'center', gap: 8 }}>
					<StatusTag
						status={
							isSuccess ? 'completed' : isPartial ? 'review' : isFailed || hasError ? 'failed' : 'pending'
						}
						label={isSuccess ? 'Completed' : isPartial ? 'Completed with warnings' : isFailed ? 'Failed' : 'Unknown'}
					/>
					<Tag style={{ fontSize: 11, margin: 0 }}>{SOURCE_FORMAT_LABELS[sourceFormat]}</Tag>
					<Text type="secondary" style={{ fontSize: 11 }}>
						ID: {jobStatus.import_uuid}
					</Text>
				</div>
			</div>

			{warnings.length > 0 && (
				<Alert
					type="warning"
					showIcon
					message={`${warnings.length} import warning${warnings.length === 1 ? '' : 's'} — some values could not be mapped`}
					description={
						<Space direction="vertical" size={4} style={{ width: '100%' }}>
							{warnings.map((warning) => (
								<Text key={`${warning.code}|${warning.field}`} style={{ fontSize: 12 }}>
									<Text strong style={{ fontSize: 12 }}>
										{warning.field}
									</Text>
									{' — '}
									{warning.message || warning.code}
								</Text>
							))}
						</Space>
					}
				/>
			)}

			<Divider style={{ margin: '12px 0' }} />

			<Row gutter={16}>
				<Col xs={24} sm={12} md={6}>
					<Statistic
						title={<Text style={{ fontSize: 12 }}>Total Records</Text>}
						value={totalRecords || 0}
						prefix={<ExperimentOutlined />}
						valueStyle={{ fontSize: 18, fontWeight: 700 }}
					/>
				</Col>
				<Col xs={24} sm={12} md={6}>
					<Statistic
						title={<Text style={{ fontSize: 12 }}>Sections Completed</Text>}
						value={jobStatus.sections_completed}
						valueStyle={{ fontSize: 18, fontWeight: 700, color: 'var(--mg-success)' }}
					/>
				</Col>
				<Col xs={24} sm={12} md={6}>
					<Statistic
						title={<Text style={{ fontSize: 12 }}>Sections Failed</Text>}
						value={jobStatus.sections_failed}
						valueStyle={{
							fontSize: 18,
							fontWeight: 700,
							color: jobStatus.sections_failed > 0 ? 'var(--mg-danger)' : 'var(--mg-text)',
						}}
					/>
				</Col>
				<Col xs={24} sm={12} md={6}>
					<Statistic
						title={<Text style={{ fontSize: 12 }}>Duration</Text>}
						value={duration}
						valueStyle={{ fontSize: 18, fontWeight: 700 }}
					/>
				</Col>
			</Row>

			{jobStatus.demographics_snapshot && (
				<SectionCard
					title={
						<Space size={8}>
							<UserOutlined />
							<span>Patient Information</span>
						</Space>
					}
				>
					<Descriptions size="small" column={{ xs: 1, sm: 2 }} colon={false}>
						{jobStatus.demographics_snapshot.first_name && (
							<Descriptions.Item label={<Text strong style={{ fontSize: 12 }}>Full Name</Text>}>
								<Text style={{ fontSize: 12 }}>
									{[jobStatus.demographics_snapshot.first_name, jobStatus.demographics_snapshot.last_name]
										.filter(Boolean)
										.join(' ')}
								</Text>
							</Descriptions.Item>
						)}
						{jobStatus.demographics_snapshot.date_of_birth && (
							<Descriptions.Item label={<Text strong style={{ fontSize: 12 }}>Date of Birth</Text>}>
								<Text style={{ fontSize: 12 }}>{jobStatus.demographics_snapshot.date_of_birth}</Text>
							</Descriptions.Item>
						)}
						{jobStatus.demographics_snapshot.sex && (
							<Descriptions.Item label={<Text strong style={{ fontSize: 12 }}>Sex</Text>}>
								<Text style={{ fontSize: 12 }}>{jobStatus.demographics_snapshot.sex}</Text>
							</Descriptions.Item>
						)}
						{jobStatus.demographics_snapshot.medicare_number && (
							<Descriptions.Item label={<Text strong style={{ fontSize: 12 }}>Medicare No.</Text>}>
								<Text style={{ fontSize: 12, fontFamily: 'monospace' }}>
									{jobStatus.demographics_snapshot.medicare_number}
								</Text>
							</Descriptions.Item>
						)}
						{jobStatus.demographics_snapshot.email && (
							<Descriptions.Item label={<Text strong style={{ fontSize: 12 }}>Email</Text>}>
								<Text style={{ fontSize: 12 }}>{jobStatus.demographics_snapshot.email}</Text>
							</Descriptions.Item>
						)}
						{jobStatus.demographics_snapshot.phone && (
							<Descriptions.Item label={<Text strong style={{ fontSize: 12 }}>Phone</Text>}>
								<Text style={{ fontSize: 12 }}>{jobStatus.demographics_snapshot.phone}</Text>
							</Descriptions.Item>
						)}
						{jobStatus.demographics_snapshot.address && (
							<Descriptions.Item label={<Text strong style={{ fontSize: 12 }}>Address</Text>} span={2}>
								<Text style={{ fontSize: 12 }}>{jobStatus.demographics_snapshot.address}</Text>
							</Descriptions.Item>
						)}
					</Descriptions>
				</SectionCard>
			)}

			<SectionCard
				title={
					<Space size={8}>
						<ExperimentOutlined />
						<span>Imported Records</span>
					</Space>
				}
			>
				<Collapse
					accordion
					items={[
						{
							key: 'results',
							label: (
								<Space size={8}>
									<ExperimentOutlined />
									<span>Pathology Results</span>
									<Badge count={results.length} style={{ backgroundColor: 'var(--mg-primary)' }} />
								</Space>
							),
							children: (
								<DataTable<ResultRecord>
									bare
									dataSource={results}
									columns={resultsColumns}
									rowKey="id"
									pagination={false}
									emptyTitle="No test results imported"
									minHeight={160}
								/>
							),
						},
						{
							key: 'medications',
							label: (
								<Space size={8}>
									<MedicineBoxOutlined />
									<span>Medications</span>
									<Badge count={medications.length} style={{ backgroundColor: 'var(--mg-primary)' }} />
								</Space>
							),
							children: (
								<DataTable<MedicationRecord>
									bare
									dataSource={medications}
									columns={medicationColumns}
									rowKey="id"
									pagination={false}
									emptyTitle="No medications imported"
									minHeight={160}
								/>
							),
						},
						// TODO: fix the data importing of appointments and make this seciton only visible if hte practitioner subscription is for xoe core not peptide only
						// {
						// 	key: 'appointments',
						// 	label: (
						// 		<Space size={8}>
						// 			<EditOutlined />
						// 			<span>Appointments & Notes</span>
						// 			<Badge count={appointments.length} style={{ backgroundColor: 'var(--mg-primary)' }} />
						// 		</Space>
						// 	),
						// 	children: (
						// 		<DataTable<AppointmentRecord>
						// 			bare
						// 			dataSource={appointments}
						// 			columns={appointmentColumns}
						// 			rowKey="id"
						// 			pagination={false}
						// 			emptyTitle="No appointments or notes imported"
						// 			minHeight={160}
						// 		/>
						// 	),
						// },
					]}
				/>
			</SectionCard>

			{isPartial &&
				jobStatus.section_results &&
				Object.entries(jobStatus.section_results).some(([, r]) => !r.success && r.error) && (
					<SectionCard
						title={
							<Space size={8}>
								<WarningOutlined style={{ color: 'var(--mg-warning)' }} />
								<span>Section Errors</span>
							</Space>
						}
					>
						<Space direction="vertical" size={12} style={{ width: '100%' }}>
							{Object.entries(jobStatus.section_results)
								.filter(([, r]) => !r.success && r.error)
								.map(([sectionKey, result]) => (
									<Alert
										key={sectionKey}
										type="warning"
										message={`${sectionKey.replace(/_/g, ' ')}: ${result.error}`}
										showIcon
									/>
								))}
						</Space>
					</SectionCard>
				)}

			{jobStatus.error_summary && (
				<Alert type="error" message="Fatal Error" description={jobStatus.error_summary} showIcon />
			)}

			{pollError && <Alert type="warning" message="Connection Lost" description={pollError} showIcon />}
		</Space>
	);
};

export default ImportSuccessModal;
